import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PlusIcon,
  BuildingStorefrontIcon,
  ArrowTrendingUpIcon,
  TruckIcon,
  ArchiveBoxIcon,
  MapPinIcon,
  ChartBarIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  PhoneIcon,
  ArrowPathIcon,
  XMarkIcon,
  CalendarDaysIcon,
  ClockIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  ArrowsRightLeftIcon,
  ViewColumnsIcon
} from '@heroicons/react/24/outline';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';
import TransferForm from '../../components/warehouses/TransferForm';


const WarehousesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { warehouses, sales, purchases, stockItems, getProducts, addWarehouse, updateWarehouse, deleteWarehouse } = useAppData();
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [transferHistory, setTransferHistory] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Function to load transfer history from multiple sources
  const loadTransferHistory = useCallback(() => {
    const transfers = [];
    
    // 1. Try to load transfers from stockItems (for API transfers)
    if (stockItems && stockItems.length > 0) {
      const transferMoves = stockItems.filter(item => 
        item.type === 'transfert' || 
        (item.notes && item.notes.toLowerCase().includes('transfert'))
      );
      
      transferMoves.forEach(move => {
        transfers.push({
          id: move.id || `mov-${Date.now()}`,
          date: move.date || new Date().toISOString(),
          sourceName: move.sourceWarehouse ? 
            warehouses.find(w => w.id === move.sourceWarehouse)?.name || 'Entrepôt source' :
            'Entrepôt source',
          destinationName: move.destinationWarehouse ?
            warehouses.find(w => w.id === move.destinationWarehouse)?.name || 'Entrepôt destination' :
            'Entrepôt destination',
          sourceWarehouse: move.sourceWarehouse,
          destinationWarehouse: move.destinationWarehouse,
          items: move.items || [],
          notes: move.notes,
          reference: move.reference
        });
      });
    }
    
    // 2. Try to load transfers from localStorage as backup
    try {
      const localTransfers = localStorage.getItem('stockpro_transfers');
      if (localTransfers) {
        const parsedTransfers = JSON.parse(localTransfers);
        if (Array.isArray(parsedTransfers) && parsedTransfers.length > 0) {
          // Check for duplicates before adding
          parsedTransfers.forEach(transfer => {
            if (!transfers.some(t => t.id === transfer.id)) {
              transfers.push(transfer);
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading transfers from localStorage:', error);
    }
    
    // Sort by date, most recent first
    transfers.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    setTransferHistory(transfers);
    return transfers;
  }, [stockItems, warehouses]);
  
  useEffect(() => {
    // Load transfers when component mounts or stockItems change
    loadTransferHistory();
    
    // Listen for events that should trigger a refresh
    const handleInventoryUpdate = () => {
      console.log('WarehousesPage received inventory update event');
      setRefreshTrigger(prev => prev + 1);
      loadTransferHistory();
    };
    
    // Add event listeners for inventory updates
    window.addEventListener('product-stock-updated', handleInventoryUpdate);
    window.addEventListener('transfer-completed', handleInventoryUpdate);
    window.addEventListener('warehouse-inventory-updated', handleInventoryUpdate);
    
    // Clean up event listeners
    return () => {
      window.removeEventListener('product-stock-updated', handleInventoryUpdate);
      window.removeEventListener('transfer-completed', handleInventoryUpdate);
      window.removeEventListener('warehouse-inventory-updated', handleInventoryUpdate);
    };
  }, [loadTransferHistory, refreshTrigger]);
  
  useEffect(() => {
    // Simulating API call delay
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Open add modal when route is /warehouses/add
  useEffect(() => {
    const isAddRoute = location.pathname.endsWith('/warehouses/add');
    setShowAddModal(isAddRoute);
    // Detect edit route
    const editPrefix = '/warehouses/edit/';
    if (location.pathname.includes(editPrefix)) {
      const id = location.pathname.substring(location.pathname.lastIndexOf('/') + 1);
      // Always use warehouses data from context
      const found = warehouses.find(w => String(w.id) === String(id));
      if (found) {
        setSelectedWarehouse(found);
        setShowEditModal(true);
      } else {
        setShowEditModal(false);
      }
    } else {
      setShowEditModal(false);
    }
  }, [location.pathname, warehouses]);

  // Using real data from AppDataContext instead of mock data

  
  // Calculate real stats from warehouses data
  const stats = useMemo(() => {
    if (!warehouses || warehouses.length === 0) {
      return {
        totalWarehouses: 0,
        totalCapacity: '0 m²',
        averageOccupancy: 0,
        totalItems: 0
      };
    }
    
    // Calculate total capacity from real data
    let totalCapacityValue = 0;
    let totalOccupancyRate = 0;
    let totalItems = 0;
    
    // Get all products and sales/purchases data
    const allProducts = getProducts();
    const allSales = sales || [];
    const allPurchases = purchases || [];
    
    // Track unique products in each warehouse to prevent double-counting
    const warehouseUniqueProducts = {};
    
    // Map to track product counts per warehouse
    const warehouseProductCounts = {};
    
    // Initialize product tracking - use string keys for consistency
    warehouses.forEach(warehouse => {
      const warehouseIdStr = String(warehouse.id);
      warehouseUniqueProducts[warehouseIdStr] = new Set();
      warehouseProductCounts[warehouseIdStr] = 0;
    });
    
    // First pass: collect products that have this warehouse as their primary location
    allProducts.forEach(product => {
      if (product.warehouseId) {
        const warehouseIdStr = String(product.warehouseId);
        if (warehouseUniqueProducts[warehouseIdStr]) {
          warehouseUniqueProducts[warehouseIdStr].add(product.id);
        }
      }
      
      // Also check warehouseIds array if it exists
      if (product.warehouseIds && Array.isArray(product.warehouseIds)) {
        product.warehouseIds.forEach(whId => {
          const warehouseIdStr = String(whId);
          if (warehouseUniqueProducts[warehouseIdStr]) {
            warehouseUniqueProducts[warehouseIdStr].add(product.id);
          }
        });
      }
      
      // Check warehouseStock object if it exists
      if (product.warehouseStock && typeof product.warehouseStock === 'object') {
        Object.keys(product.warehouseStock).forEach(whId => {
          if (product.warehouseStock[whId] > 0 && warehouseUniqueProducts[whId]) {
            warehouseUniqueProducts[whId].add(product.id);
          }
        });
      }
    });
    
    // Add products from purchases (only if not already counted)
    allPurchases.forEach(purchase => {
      if (purchase.warehouseId && purchase.items && purchase.items.length > 0) {
        const warehouseIdStr = String(purchase.warehouseId);
        purchase.items.forEach(item => {
          if (item.id && warehouseUniqueProducts[warehouseIdStr]) {
            warehouseUniqueProducts[warehouseIdStr].add(item.id);
          }
        });
      }
    });
    
    // Add products from stock movements (entries)
    const stockItems = JSON.parse(localStorage.getItem('stockpro_stock') || '[]');
    stockItems.forEach(item => {
      if (item.type === 'entrée' && item.productId) {
        // Check all possible warehouse ID fields
        const warehouseIds = [
          item.warehouse, 
          item.location, 
          item.sourceWarehouse, 
          item.destinationWarehouse
        ].filter(id => id); // Filter out undefined/null
        
        warehouseIds.forEach(whId => {
          const warehouseIdStr = String(whId);
          if (warehouseUniqueProducts[warehouseIdStr]) {
            warehouseUniqueProducts[warehouseIdStr].add(item.productId);
          }
        });
      }
    });
    
    // Calculate the final count of unique products per warehouse
    Object.keys(warehouseUniqueProducts).forEach(warehouseId => {
      warehouseProductCounts[warehouseId] = warehouseUniqueProducts[warehouseId].size;
      console.log(`Warehouse ${warehouseId}: ${warehouseProductCounts[warehouseId]} unique products`);
    });
    
    console.log("Warehouse unique products:", warehouseUniqueProducts);
    console.log("Warehouse product counts:", warehouseProductCounts);
    
    warehouses.forEach(warehouse => {
      // Extract numeric capacity value
      const capacityMatch = warehouse.capacity ? warehouse.capacity.match(/(\d+)/) : null;
      if (capacityMatch) {
        totalCapacityValue += parseInt(capacityMatch[1]);
      }
      
      // Calculate real occupancy rate based on products in this warehouse
      const warehouseItems = warehouseProductCounts[warehouse.id] || 0;
      const capacityValue = capacityMatch ? parseInt(capacityMatch[1]) : 100;
      const occupancyRate = Math.min(100, Math.round((warehouseItems / (capacityValue / 10)) * 100) / 100);
      
      totalOccupancyRate += occupancyRate;
      
      // Use real item count from warehouseProductCounts
      const itemCount = warehouseItems;
      totalItems += itemCount;
    });
    
    return {
      totalWarehouses: warehouses.length,
      totalCapacity: `${totalCapacityValue} m²`,
      averageOccupancy: Math.round(totalOccupancyRate / warehouses.length) || 0,
      totalItems: totalItems,
      warehouseProductCounts: warehouseProductCounts // Add this for use in warehouse details
    };
  }, [warehouses, sales, purchases, getProducts]);

  // Add new functions
  const handleAddWarehouse = () => {
    navigate('/warehouses/add');
  };
  
  const handleViewDetails = (id) => {
    // Always use warehouses from AppDataContext
    const warehouse = warehouses.find(w => String(w.id) === String(id));
    if (warehouse) {
      setSelectedWarehouse(warehouse);
      setShowDetailsModal(true);
    }
  };
  
  const handleEditWarehouse = (id) => {
    navigate(`/warehouses/edit/${id}`);
  };
  
  const handleDeleteWarehouse = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet entrepôt ?')) {
      try {
        const result = await deleteWarehouse(id);
        if (result) {
          toast.success('Entrepôt supprimé avec succès');
          // Force refresh to update UI
          setRefreshTrigger(prev => prev + 1);
        } else {
          toast.error("Erreur lors de la suppression de l'entrepôt");
        }
      } catch (error) {
        console.error("Erreur lors de la suppression de l'entrepôt:", error);
        toast.error("Erreur lors de la suppression: " + (error.message || "Erreur inconnue"));
      }
    }
  };

  // (Chat removed)

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-DZ', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'maintenance': return 'bg-yellow-500/20 text-yellow-400';
      case 'inactive': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'active': return 'Actif';
      case 'maintenance': return 'En maintenance';
      case 'inactive': return 'Inactif';
      default: return 'Inconnu';
    }
  };

  // Always use warehouses from AppDataContext
  const filteredWarehouses = warehouses.filter(warehouse => {
    const searchTermMatch = warehouse.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           warehouse.location?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (activeTab === 'all') return searchTermMatch;
    if (activeTab === 'active') return warehouse.status === 'active' && searchTermMatch;
    if (activeTab === 'maintenance') return warehouse.status === 'maintenance' && searchTermMatch;
    
    return searchTermMatch;
  });

  return (
    <div className="flex flex-col space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Gestion des Entrepôts</h1>
          <p className="text-white/60 mt-1">Gérez vos espaces de stockage et suivez leur utilisation</p>
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={() => setShowTransferModal(true)}
            className="flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white rounded-xl"
          >
            <ArrowsRightLeftIcon className="h-5 w-5 mr-2" />
            Transférer des produits
          </button>
        
        <button
          onClick={handleAddWarehouse}
          className="flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Ajouter un Entrepôt
        </button>
        </div>
      </div>
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Nombre d'Entrepôts</p>
              <p className="text-2xl font-bold text-white mt-2">{stats.totalWarehouses}</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-blue-500/20 to-blue-700/20 rounded-xl">
              <BuildingStorefrontIcon className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Capacité Totale</p>
              <p className="text-2xl font-bold text-white mt-2">{stats.totalCapacity}</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-green-500/20 to-green-700/20 rounded-xl">
              <ArchiveBoxIcon className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Taux d'Occupation</p>
              <p className="text-2xl font-bold text-white mt-2">{stats.averageOccupancy}%</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-yellow-500/20 to-yellow-700/20 rounded-xl">
              <ChartBarIcon className="h-6 w-6 text-yellow-400" />
            </div>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Articles Stockés</p>
              <p className="text-2xl font-bold text-white mt-2">{stats.totalItems.toLocaleString()}</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-purple-500/20 to-purple-700/20 rounded-xl">
              <TruckIcon className="h-6 w-6 text-purple-400" />
            </div>
          </div>
        </motion.div>
        
        {/* Alerts card removed */}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            <span>Tous</span>
          </button>
          
          <button
            onClick={() => setActiveTab('active')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'active'
                ? 'bg-green-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            <span>Actifs</span>
          </button>
          
          <button
            onClick={() => setActiveTab('maintenance')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              activeTab === 'maintenance'
                ? 'bg-yellow-600 text-white'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            }`}
          >
            <span>En maintenance</span>
          </button>
          
          {/* Alerts filter removed */}
        </div>
        
        <div className="w-full md:w-64 relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un entrepôt..."
            className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            <ChartBarIcon className="h-5 w-5 text-white/40" />
          </div>
        </div>
      </div>
      

      
      {/* Main Content - Warehouses Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading ? (
          Array(3).fill(0).map((_, index) => (
            <div key={index} className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 h-64 animate-pulse" />
          ))
        ) : filteredWarehouses.length === 0 ? (
          <div className="col-span-3 flex flex-col items-center justify-center py-12">
            <BuildingStorefrontIcon className="h-16 w-16 text-white/20 mb-4" />
            <h3 className="text-xl font-semibold text-white">Aucun entrepôt trouvé</h3>
            <p className="text-white/60 mt-2">Ajoutez un nouvel entrepôt ou modifiez vos filtres de recherche</p>
            <button
              onClick={handleAddWarehouse}
              className="mt-6 flex items-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Ajouter un Entrepôt
            </button>
          </div>
        ) : (
          filteredWarehouses.map((warehouse) => (
            <motion.div
              key={warehouse.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden flex flex-col"
            >
              <div className="p-6 border-b border-white/10">
                <div className="flex justify-between items-start">
                  <div className="flex items-center">
                    <BuildingStorefrontIcon className="h-6 w-6 text-blue-400 mr-3" />
                    <div>
                      <h3 className="text-xl font-semibold text-white">{warehouse.name}</h3>
                      <div className="flex items-center mt-1 text-white/60 text-sm">
                        <MapPinIcon className="h-4 w-4 mr-1" />
                        <span>{warehouse.location}</span>
                      </div>
                    </div>
                  </div>
                  
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(warehouse.status)}`}>
                    {getStatusText(warehouse.status)}
                  </span>
                </div>
                
                <div className="mt-4">
                  <div className="flex justify-between items-center text-sm mb-1">
                    <span className="text-white/70">Occupation</span>
                    <span className={`font-medium ${
                      (() => {
                        // Calculate real occupancy based on product counts and capacity
                        const itemCount = stats.warehouseProductCounts?.[warehouse.id] || 0;
                        const capacityMatch = warehouse.capacity ? warehouse.capacity.match(/(\d+)/) : null;
                        const capacityValue = capacityMatch ? parseInt(capacityMatch[1]) : 100;
                        const occupancyRate = Math.min(100, Math.round((itemCount / (capacityValue / 10)) * 100) / 100);
                        
                        return occupancyRate > 80 ? 'text-red-400' : 
                               occupancyRate > 60 ? 'text-yellow-400' : 
                               'text-green-400';
                      })()
                    }`}>
                      {(() => {
                        // Calculate real occupancy based on product counts and capacity
                        const itemCount = stats.warehouseProductCounts?.[warehouse.id] || 0;
                        const capacityMatch = warehouse.capacity ? warehouse.capacity.match(/(\d+)/) : null;
                        const capacityValue = capacityMatch ? parseInt(capacityMatch[1]) : 100;
                        const occupancyRate = Math.min(100, Math.round((itemCount / (capacityValue / 10)) * 100) / 100);
                        
                        return occupancyRate;
                      })()}%
                    </span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        (() => {
                          // Calculate real occupancy based on product counts and capacity
                          const itemCount = stats.warehouseProductCounts?.[warehouse.id] || 0;
                          const capacityMatch = warehouse.capacity ? warehouse.capacity.match(/(\d+)/) : null;
                          const capacityValue = capacityMatch ? parseInt(capacityMatch[1]) : 100;
                          const occupancyRate = Math.min(100, Math.round((itemCount / (capacityValue / 10)) * 100) / 100);
                          
                          return occupancyRate > 80 ? 'bg-red-500' : 
                                 occupancyRate > 60 ? 'bg-yellow-500' : 'bg-green-500';
                        })()
                      }`}
                      style={{ width: `${(() => {
                        // Calculate real occupancy based on product counts and capacity
                        const itemCount = stats.warehouseProductCounts?.[warehouse.id] || 0;
                        const capacityMatch = warehouse.capacity ? warehouse.capacity.match(/(\d+)/) : null;
                        const capacityValue = capacityMatch ? parseInt(capacityMatch[1]) : 100;
                        const occupancyRate = Math.min(100, Math.round((itemCount / (capacityValue / 10)) * 100) / 100);
                        
                        return occupancyRate;
                      })()}%` }}
                    />
                  </div>
                </div>
                
                <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-white/60">Capacité</p>
                    <p className="text-white font-medium">{warehouse.capacity}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3">
                    <p className="text-white/60">Articles</p>
                    <p className="text-white font-medium">{(stats.warehouseProductCounts?.[String(warehouse.id)] || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-3 flex items-center justify-between mt-auto border-t border-white/10">
                <div className="flex items-center text-sm text-white/60">
                  <UserIcon className="h-4 w-4 mr-1" />
                  <span>{warehouse.manager}</span>
                </div>
                
                <div className="flex space-x-1">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/warehouses/inventory/${warehouse.id}`);
                    }}
                    className="p-2 bg-blue-500/20 rounded-lg hover:bg-blue-500/30 text-blue-300"
                    title="Voir l'inventaire"
                  >
                    <ViewColumnsIcon className="h-5 w-5" />
                  </button>
                  
                  <button 
                    onClick={() => handleViewDetails(warehouse.id)}
                    className="p-2 bg-white/10 rounded-lg hover:bg-white/20 text-white"
                    title="Voir les détails"
                  >
                    <EyeIcon className="h-5 w-5" />
                  </button>
                  
                  <button 
                    onClick={() => handleEditWarehouse(warehouse.id)}
                    className="p-2 bg-white/10 rounded-lg hover:bg-white/20 text-white"
                    title="Modifier l'entrepôt"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  
                  <button 
                    onClick={() => handleDeleteWarehouse(warehouse.id)}
                    className="p-2 bg-red-500/20 rounded-lg hover:bg-red-500/30 text-red-400"
                    title="Supprimer l'entrepôt"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
      
      {/* Add Warehouse Modal */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => navigate('/warehouses')}></div>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              className="bg-gray-900/95 border border-white/10 rounded-3xl w-full max-w-2xl z-10 overflow-hidden"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Ajouter un Entrepôt</h2>
                <button onClick={() => navigate('/warehouses')} className="p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white">
                  <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
              <AddWarehouseForm onCancel={() => navigate('/warehouses')} onSaved={() => navigate('/warehouses')} addWarehouse={addWarehouse} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Warehouse Modal */}
      <AnimatePresence>
        {showEditModal && selectedWarehouse && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => navigate('/warehouses')}></div>
            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              className="bg-gray-900/95 border border-white/10 rounded-3xl w-full max-w-2xl z-10 overflow-hidden"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">Modifier l'Entrepôt</h2>
                <button onClick={() => navigate('/warehouses')} className="p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white">
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <EditWarehouseForm warehouse={selectedWarehouse} onCancel={() => navigate('/warehouses')} onSaved={() => navigate('/warehouses')} updateWarehouse={updateWarehouse} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Chat panel removed as requested */}
      
      {/* Warehouse Details Modal */}
      <AnimatePresence>
        {showDetailsModal && selectedWarehouse && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowDetailsModal(false)}></div>
            
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-gray-900/95 border border-white/10 rounded-3xl w-full max-w-4xl z-10 max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="p-6 border-b border-white/10 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white flex items-center">
                  <BuildingStorefrontIcon className="h-7 w-7 text-blue-400 mr-3" />
                  {selectedWarehouse.name}
                </h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="p-2 hover:bg-white/10 rounded-full text-white/60 hover:text-white"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <InformationCircleIcon className="h-5 w-5 mr-2 text-blue-400" />
                      Informations générales
                    </h3>
                    
                    <div className="space-y-4">
                      <div className="bg-white/5 p-4 rounded-xl">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-white/60 text-sm">Statut</p>
                            <p className={`font-medium ${
                              selectedWarehouse.status === 'active' ? 'text-green-400' : 
                              selectedWarehouse.status === 'maintenance' ? 'text-yellow-400' : 
                              'text-red-400'
                            }`}>
                              {getStatusText(selectedWarehouse.status)}
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-white/60 text-sm">Taux d'occupation</p>
                            <p className={`font-medium ${
                              selectedWarehouse.occupancyRate > 80 ? 'text-red-400' : 
                              selectedWarehouse.occupancyRate > 60 ? 'text-yellow-400' : 
                              'text-green-400'
                            }`}>
                              {selectedWarehouse.occupancyRate}%
                            </p>
                          </div>
                          
                          <div>
                            <p className="text-white/60 text-sm">Capacité</p>
                            <p className="text-white">{selectedWarehouse.capacity}</p>
                          </div>
                          
                          <div>
                            <p className="text-white/60 text-sm">Articles stockés</p>
                            <p className="text-white">{(stats.warehouseProductCounts?.[selectedWarehouse.id] || 0).toLocaleString()}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white/5 p-4 rounded-xl">
                        <h4 className="text-white font-medium mb-3">Localisation</h4>
                        <p className="text-white mb-2">{selectedWarehouse.address}</p>
                        <div className="flex items-center text-white/60">
                          <MapPinIcon className="h-4 w-4 mr-1.5" />
                          <span>{selectedWarehouse.location}</span>
                        </div>
                      </div>
                      
                      <div className="bg-white/5 p-4 rounded-xl">
                        <h4 className="text-white font-medium mb-3">Contact</h4>
                        <div className="space-y-2">
                          <div className="flex items-center text-white">
                            <UserIcon className="h-4 w-4 mr-2 text-white/60" />
                            <span>{selectedWarehouse.manager}</span>
                          </div>
                          <div className="flex items-center text-white">
                            <PhoneIcon className="h-4 w-4 mr-2 text-white/60" />
                            <span>{selectedWarehouse.phone}</span>
                          </div>
                          <div className="flex items-center text-white">
                            <DocumentTextIcon className="h-4 w-4 mr-2 text-white/60" />
                            <span>{selectedWarehouse.email}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-medium text-white mb-4 flex items-center">
                      <ArrowPathIcon className="h-5 w-5 mr-2 text-yellow-400" />
                      Mouvements récents
                    </h3>
                    
                    <div className="space-y-3">
                      {selectedWarehouse.movements.map((movement, idx) => (
                        <div key={idx} className="bg-white/5 p-3 rounded-xl flex items-center justify-between">
                          <div className="flex items-center">
                            <span className={`inline-flex items-center justify-center p-1.5 rounded-lg ${
                              movement.type === 'entrée' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {movement.type === 'entrée' ? 
                                <ArrowTrendingUpIcon className="h-5 w-5" /> : 
                                <TruckIcon className="h-5 w-5" />
                              }
                            </span>
                            <div className="ml-3">
                              <p className="text-white text-sm font-medium">{movement.product}</p>
                              <div className="flex items-center text-white/60 text-xs mt-0.5">
                                <CalendarDaysIcon className="h-3.5 w-3.5 mr-1" />
                                <span>{movement.date}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <span className={`font-medium text-sm ${
                              movement.type === 'entrée' ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {movement.type === 'entrée' ? '+' : '-'}{movement.quantity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Communications section removed as requested */}
                          </div>
                </div>
              </div>
              
              <div className="p-4 border-t border-white/10 flex justify-between">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleDeleteWarehouse(selectedWarehouse.id);
                  }}
                  className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white rounded-xl flex items-center"
                >
                  <TrashIcon className="h-5 w-5 mr-2" />
                  Supprimer
                </button>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl"
                  >
                    Fermer
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      handleEditWarehouse(selectedWarehouse.id);
                    }}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl flex items-center"
                  >
                    <PencilIcon className="h-5 w-5 mr-2" />
                    Modifier
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* TransferForm Modal */}
      <AnimatePresence>
        {showTransferModal && (
          <TransferForm onClose={() => setShowTransferModal(false)} />
        )}
      </AnimatePresence>
    </div>
  );
};

const AddWarehouseForm = ({ onCancel, onSaved, addWarehouse }) => {
  const [form, setForm] = useState({
    name: '',
    location: '',
    capacity: '',
    manager: '',
    phone: '',
    email: '',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      await addWarehouse({
        id: Date.now(),
        name: form.name.trim(),
        location: form.location.trim(),
        capacity: form.capacity ? `${form.capacity} m²` : '—',
        occupancyRate: 0,
        itemCount: 0,
        manager: form.manager.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        status: form.status,
        alerts: 0,
        movements: [],
        messages: []
      });
      toast.success('Entrepôt ajouté');
      onSaved();
    } catch (err) {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-white/80 text-sm mb-1">Nom de l'entrepôt</label>
          <input name="name" value={form.name} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Localisation</label>
          <input name="location" value={form.location} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Capacité (m²)</label>
          <input type="number" name="capacity" value={form.capacity} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Responsable</label>
          <input name="manager" value={form.manager} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Téléphone</label>
          <input name="phone" value={form.phone} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Email</label>
          <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Statut</label>
          <select name="status" value={form.status} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="active">Actif</option>
            <option value="maintenance">En maintenance</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end space-x-3 pt-2">
        <button type="button" onClick={onCancel} className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl">Annuler</button>
        <button type="submit" disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl disabled:opacity-50">
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
};

const EditWarehouseForm = ({ warehouse, onCancel, onSaved, updateWarehouse }) => {
  const [form, setForm] = useState({
    name: warehouse?.name || '',
    location: warehouse?.location || '',
    capacity: (warehouse?.capacity || '').toString().replace(' m²',''),
    manager: warehouse?.manager || '',
    phone: warehouse?.phone || '',
    email: warehouse?.email || '',
    status: warehouse?.status || 'active'
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      await updateWarehouse(warehouse.id, {
        name: form.name.trim(),
        location: form.location.trim(),
        capacity: form.capacity ? `${form.capacity} m²` : warehouse.capacity,
        manager: form.manager.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        status: form.status,
      });
      toast.success('Entrepôt mis à jour');
      onSaved();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-white/80 text-sm mb-1">Nom de l'entrepôt</label>
          <input name="name" value={form.name} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Localisation</label>
          <input name="location" value={form.location} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Capacité (m²)</label>
          <input type="number" name="capacity" value={form.capacity} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Responsable</label>
          <input name="manager" value={form.manager} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Téléphone</label>
          <input name="phone" value={form.phone} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Email</label>
          <input type="email" name="email" value={form.email} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div>
          <label className="block text-white/80 text-sm mb-1">Statut</label>
          <select name="status" value={form.status} onChange={handleChange} className="w-full p-2.5 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="active">Actif</option>
            <option value="maintenance">En maintenance</option>
            <option value="inactive">Inactif</option>
          </select>
        </div>
      </div>
      <div className="flex justify-end space-x-3 pt-2">
        <button type="button" onClick={onCancel} className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl">Annuler</button>
        <button type="submit" disabled={saving} className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white rounded-xl disabled:opacity-50">
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
};

export default WarehousesPage;